import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  ArrowLeft,
  Send,
  ChevronDown,
  ChevronUp,
  Hash,
  Type,
  Calendar,
  Building,
  ShieldCheck,
  Wallet,
  Landmark,
  BadgePercent,
  Users,
  CheckSquare,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const monthOptions = [
  { text: "Select", value: "Select" },
  { text: "January", value: "1" },
  { text: "February", value: "2" },
  { text: "March", value: "3" },
  { text: "April", value: "4" },
  { text: "May", value: "5" },
  { text: "June", value: "6" },
  { text: "July", value: "7" },
  { text: "August", value: "8" },
  { text: "September", value: "9" },
  { text: "October", value: "10" },
  { text: "November", value: "11" },
  { text: "December", value: "12" },
];

const PayrollEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ consistent button colors
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // session-like (from your app)
  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || ""; // after login
  const branchType = localStorage.getItem("branchType") || "HO"; // must exist in your app: HO/others
    const loginId = localStorage.getItem("userId") || ""; // after login


  const screenBadge = useMemo(() => "INFRA SCREEN : C-20/1", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  

  // ---------------- meta / filters ----------------
  const [loading, setLoading] = useState(false);
  const [canGenerate, setCanGenerate] = useState(true);

    // ---------------- grid ----------------
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination (ASP had 100/page; we keep UI paging for performance)
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ✅ NOW define button classes AFTER selectedIds exists
const primaryBtn =
  `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
  `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

const ghostBtn =
  `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
  (isDark
    ? "border-white/10 text-white hover:bg-white/10"
    : "border-gray-200 text-gray-800 hover:bg-gray-50");

const submitBtn =
  `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
  `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
  (selectedIds.length === 0 || !canGenerate || loading
    ? "bg-gray-400 cursor-not-allowed"
    : "bg-emerald-600 hover:bg-emerald-700");


  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [years, setYears] = useState(["Select"]);

  const [branchId, setBranchId] = useState("Select");
  const [year, setYear] = useState("Select");
  const [month, setMonth] = useState("Select");

  const [pf, setPf] = useState("Select");
  const [payMode, setPayMode] = useState("Select");
  const [esi, setEsi] = useState("Select");
  const [advance, setAdvance] = useState("Select");
  const [others, setOthers] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");

  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  const [showFilters, setShowFilters] = useState(true);

  // visibility controls like ASP FieldsEnable()
  const [vis, setVis] = useState({
    paidBasicVisible: true,
    conveyanceVisible: true,
    educationalVisible: true,
    medicalVisible: false,
    hraVisible: true,
    lopAmtVisible: true,
    gradeVisible: true,
  });


  // ---------------- helpers ----------------
  const monthName = (mVal) => {
    const m = String(mVal || "Select");
    const found = monthOptions.find((x) => x.value === m);
    return found?.text || m;
  };

  const num = (v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };

  const calcWorkingDays = (y, m) => {
    const yy = Number(y);
    const mm = Number(m);
    if (!Number.isFinite(yy) || !Number.isFinite(mm) || yy <= 0 || mm <= 0) return 0;
    // days in month:
    return new Date(yy, mm, 0).getDate(); // month is 1-based here
  };

  const resetGrid = () => {
    setRows([]);
    setSelectedIds([]);
    setSelectAll(false);
    setTotal(0);
    setTotalPages(1);
    setPage(1);
  };

  // ---------------- API calls ----------------
  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim()) {
      toast.warn("Login branch not found in localStorage (branchId).");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get(`${BASE_URL}/api/payroll/payroll-entry/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType || "HO"),
        },
        timeout: 60000,
      });

      if (!data?.success) {
        toast.error(data?.message || "Meta load failed");
        return;
      }

      setBranches(data.branches || []);
      setDepartments(data.departments || []);
      setYears(data.years || ["Select"]);
      setVis(data.columnVisibility || vis);

      const allow = !!data.canGeneratePayroll; // only HO
      setCanGenerate(allow);

      // defaults
      setBranchId(String(data.defaultBranchId || loginBranchId || "Select"));
      setYear(String(data.defaultYear || "Select"));
      setMonth(String(data.defaultMonth || "Select"));

      // default filters
      setPf("Select");
      setPayMode("Select");
      setEsi("Select");
      setAdvance("Select");
      setOthers("Select");
      setDepartmentId("Select");
      setEmpCode("");
      setEmpName("");

      resetGrid();

      if (!allow) {
        toast.error("You dont have rights to generate the payroll (Only HO).");
      } else {
        toast.info("Ready to generate payroll");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchGrid = async (p = 1) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (branchId === "Select") {
      toast.warn("Please Select Branch..");
      return;
    }
    if (year === "Select") {
      toast.warn("Please Select Year..");
      return;
    }
    if (month === "Select") {
      toast.warn("Please Select Month");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(`${BASE_URL}/api/payroll/payroll-entry/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: String(branchId),
          year: String(year),
          month: String(month), // numeric value "1..12"
          empCode: empCode.trim(),
          empName: empName.trim(),
          pf,
          payMode,
          esi,
          advance,
          others,
          departmentId,
          page: p,
          pageSize,
        },
        timeout: 60000,
      });

      if (!data?.success) {
        toast.error(data?.message || "Grid load failed");
        return;
      }

      const wd = calcWorkingDays(year, month);

      // normalize rows & attach editable fields like ASP:
      const normalized = (data.rows || []).map((r) => {
        const baseNet = num(r.NetPay ?? r.netpay ?? r.Netpay ?? 0, 0); // from SP
        const tdsVal = String(r.fld_tds ?? r.TDS ?? r.tds ?? "").trim();
        // ASP: on row databound -> netpay = netpayhide - tds
        const netAfterTds = baseNet - num(tdsVal || 0, 0);

        return {
          ...r,
          __employeeId: r.fld_Employeeid ?? r.EmployeeId ?? r.employeeId ?? r.fld_employeeid,
          __workingDays: wd,
          __tds: tdsVal === "" ? "0" : tdsVal,
          __othersDeduction: "0",
          __netpayHide: baseNet,
          __netpay: netAfterTds,
          __lopDays: num(r.fld_lopdays ?? r.LOPDays ?? r.lopdays ?? 0, 0),
        };
      });

      setRows(normalized);
      setTotal(data.total || normalized.length);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || p);

      // visibility from API (salary structure)
      if (data.columnVisibility) setVis(data.columnVisibility);

      // reset selection
      setSelectedIds([]);
      setSelectAll(false);

      toast.success(`${normalized.length} rows loaded for ${monthName(month)} ${year}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Grid load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ASP behavior: when branch/year/month changes, clear grid
  useEffect(() => {
    resetGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, year, month]);

  // ---------------- row edit: TDS / Others => netpay ----------------
  const updateRow = (employeeId, patch) => {
    setRows((prev) =>
      prev.map((r) => {
        if (String(r.__employeeId) !== String(employeeId)) return r;

        const next = { ...r, ...patch };
        const tdsVal = num(next.__tds || 0, 0);
        const othVal = num(next.__othersDeduction || 0, 0);

        // ASP: lblNetPay = netpayhide - (others + tds)
        next.__netpay = num(next.__netpayHide || 0, 0) - (tdsVal + othVal);
        return next;
      })
    );
  };

  // ---------------- selection ----------------
  const toggleSelect = (employeeId) => {
    setSelectedIds((prev) =>
      prev.includes(String(employeeId))
        ? prev.filter((x) => x !== String(employeeId))
        : [...prev, String(employeeId)]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => String(r.__employeeId)));
    setSelectAll(true);
  };

  // ---------------- submit ----------------
  const handleSubmit = async () => {
    if (!canGenerate) {
      toast.error("You dont have rights to generate the payroll (Only HO).");
      return;
    }
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (branchId === "Select") {
      toast.warn("Please Select Branch..");
      return;
    }
    if (year === "Select") {
      toast.warn("Please Select Year..");
      return;
    }
    if (month === "Select") {
      toast.warn("Please Select Month");
      return;
    }
    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to submit?");
    if (!ok) return;

    // build payload exactly like ASP loop uses
    const selectedRows = rows
      .filter((r) => selectedIds.includes(String(r.__employeeId)))
      .map((r) => ({
        employeeId: r.__employeeId,
        workingDays: r.__workingDays,
        daysWorked: r.DaysWorked ?? r.daysworked ?? r.Daysworked ?? r.fld_daysworked ?? "0",
        absence: r.Absence ?? r.absence ?? r.fld_absence ?? "0",
        lopDays: r.fld_lopdays ?? r.__lopDays ?? "0",

        grossSalary: r.grosssalary ?? r.GrossSalary ?? r.grossSalary ?? "0",
        basicPay: r.BasicPay ?? r.basicpay ?? r.fld_basicpay ?? "0",
        paidBasic: r.PaidBasic ?? r.paidbasic ?? "0",

        hra: r.HRA ?? r.hra ?? "0",
        conveyance: r.Conveyance ?? r.conveyance ?? "0",
        educational: r.Educational ?? r.educational ?? "0",
        medical: r.Medical ?? r.medical ?? "0",

        pf: r.PF ?? r.pf ?? "0",
        esi: r.ESI ?? r.esi ?? "0",
        advance: r.advance ?? r.Advance ?? "0",

        balanceLoanAmt: r.BalanceLoanAmt ?? r.balloan ?? r.balanceLoanAmt ?? "0",
        loanDetection: r.LoanDetection ?? r.loandetection ?? r.loanDetection ?? "0",

        lopAmt: r.LOPAmt ?? r.lopamt ?? r.lopAmt ?? "0",
        netPay: r.__netpay, // recalculated net pay
        otAmt: r.OT ?? r.otamt ?? r.otAmt ?? "0",

        tds: r.__tds,
        otherDeductions: r.__othersDeduction,
      }));

    // validate TDS (ASP requires typed)
    const bad = selectedRows.find((x) => String(x.tds || "").trim() === "");
    if (bad) {
      toast.error("Please Type TDS");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${BASE_URL}/api/payroll/payroll-entry/submit`,
        {
          finYearId: Number(finYearId),
          branchType: String(branchType || "HO"),
          branchId: String(branchId),
          userId: String(loginId),
          year: String(year),
          month: String(month), // numeric "1..12"
          rows: selectedRows,
        },
        { timeout: 600000 }
      );

      if (!data?.success) {
        toast.error(data?.message || "Submit failed");
        return;
      }

      toast.success(data?.message || "Inserted Successfully..");
      // ASP redirects to PayrollListing.aspx
      window.location.href = "/payroll/PayrollListing"; // change to your react route
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  // ---------------- UI ----------------
  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PAYROLL ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Payroll Generate (Add)
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search employees → edit TDS / Others → select → submit
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>

              {!canGenerate && (
                <div
                  className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                    isDark ? "bg-red-900/40 text-red-100" : "bg-red-100 text-red-800"
                  }`}
                >
                  Only HO can generate payroll
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button
                onClick={() => (window.location.href = "/payroll/payroll-listing")}
                className={ghostBtn}
                type="button"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={loadMeta} className={ghostBtn} disabled={loading} type="button">
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh Meta
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn} type="button">
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPage(1);
                fetchGrid(1);
              }}
              className={cardBase + " p-6 mb-6"}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Employee Code */}
                <div>
                  <label className={labelBase}>Employee Code</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={empCode}
                      onChange={(e) => setEmpCode(e.target.value)}
                      placeholder="Type Employee Code"
                      className={inputBase}
                    />
                  </div>
                </div>

                {/* Employee Name */}
                <div>
                  <label className={labelBase}>Employee Name</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={empName}
                      onChange={(e) => setEmpName(e.target.value)}
                      placeholder="Type Employee Name"
                      className={inputBase}
                    />
                  </div>
                </div>

                {/* Year */}
                <div>
                  <label className={labelBase}>
                    Year <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase}>
                      {(years || ["Select"]).map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Month */}
                <div>
                  <label className={labelBase}>
                    Month <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputBase}>
                      {monthOptions.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.text}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Branch */}
                <div>
                  <label className={labelBase}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Building className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      {(branches || []).map((b, idx) => {
                        const val = String(b.value ?? b.Value ?? b.id ?? b.fld_Branchid ?? b.BranchId ?? "");
                        const text =
                          b.text ?? b.Text ?? b.name ?? b.fld_BranchName ?? b.BranchName ?? `Branch ${idx + 1}`;
                        if (!val) return null;
                        return (
                          <option key={val} value={val}>
                            {text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* PF */}
                <div>
                  <label className={labelBase}>PF</label>
                  <div className="mt-2 flex items-center gap-2">
                    <ShieldCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={pf} onChange={(e) => setPf(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* Payment Mode */}
                <div>
                  <label className={labelBase}>Payment Mode</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Wallet className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={payMode} onChange={(e) => setPayMode(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      <option value="Cash">Cash</option>
                      <option value="Bank">Bank</option>
                    </select>
                  </div>
                </div>

                {/* ESI */}
                <div>
                  <label className={labelBase}>ESI</label>
                  <div className="mt-2 flex items-center gap-2">
                    <ShieldCheck className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={esi} onChange={(e) => setEsi(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* Advance */}
                <div>
                  <label className={labelBase}>Advance</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Landmark className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={advance} onChange={(e) => setAdvance(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* Others */}
                <div>
                  <label className={labelBase}>Others</label>
                  <div className="mt-2 flex items-center gap-2">
                    <BadgePercent className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={others} onChange={(e) => setOthers(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* Department (rights based in ASP; we show dropdown but you can hide if API says no) */}
                <div>
                  <label className={labelBase}>Department</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Users className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value)}
                      className={inputBase}
                    >
                      <option value="Select">Select</option>
                      {(departments || []).map((d, idx) => {
                        const val = String(d.fld_deptid ?? d.deptid ?? d.id ?? d.value ?? d.Value ?? "");
                        const text = d.fld_departmentname ?? d.deptName ?? d.text ?? d.Text ?? `Department ${idx + 1}`;
                        if (!val) return null;
                        return (
                          <option key={val} value={val}>
                            {text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              {/* actions */}
              <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setPage(1);
                    fetchGrid(1);
                  }}
                  className={primaryBtn}
                  disabled={loading}
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>

                <button
                  type="button"
                  onClick={() => {
                    // reset fields like ASP clears grid on dropdown change
                    setEmpCode("");
                    setEmpName("");
                    setPf("Select");
                    setPayMode("Select");
                    setEsi("Select");
                    setAdvance("Select");
                    setOthers("Select");
                    setDepartmentId("Select");
                    resetGrid();
                    toast.info("Filters cleared");
                  }}
                  className={ghostBtn}
                  disabled={loading}
                >
                  <RefreshCcw className="w-4 h-4" />
                  Clear
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-left font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Branch</th>

                    {vis.gradeVisible && <th className="px-4 py-4 text-center font-bold">Grade</th>}

                    <th className="px-4 py-4 text-center font-bold">Working Days</th>
                    <th className="px-4 py-4 text-center font-bold">Days Worked</th>
                    <th className="px-4 py-4 text-center font-bold">LOP</th>

                    <th className="px-4 py-4 text-center font-bold">Gross Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Basic Pay</th>

                    {vis.paidBasicVisible && <th className="px-4 py-4 text-center font-bold">Paid Basic</th>}
                    {vis.conveyanceVisible && <th className="px-4 py-4 text-center font-bold">Conveyance</th>}
                    {vis.educationalVisible && <th className="px-4 py-4 text-center font-bold">Educational</th>}
                    {vis.medicalVisible && <th className="px-4 py-4 text-center font-bold">Medical</th>}

                    {vis.hraVisible && <th className="px-4 py-4 text-center font-bold">HRA</th>}

                    <th className="px-4 py-4 text-center font-bold">PF</th>
                    <th className="px-4 py-4 text-center font-bold">ESI</th>
                    <th className="px-4 py-4 text-center font-bold">Advance</th>

                    <th className="px-4 py-4 text-center font-bold">Loan</th>
                    <th className="px-4 py-4 text-center font-bold">Loan Detection</th>
                    <th className="px-4 py-4 text-center font-bold">Balance Loan</th>

                    {vis.lopAmtVisible && <th className="px-4 py-4 text-center font-bold">LOP Amount</th>}

                    <th className="px-4 py-4 text-center font-bold">TDS</th>
                    <th className="px-4 py-4 text-center font-bold">Other Deductions</th>
                    <th className="px-4 py-4 text-center font-bold">Net Pay</th>

                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td className="px-4 py-4" colSpan={30}>
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={30} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const empId = String(r.__employeeId);

                      const lopDays = num(r.__lopDays, 0);
                      const redRow = lopDays > 0;

                      return (
                        <tr
                          key={empId}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} ${
                            redRow ? "bg-red-100/40 dark:bg-red-900/30" : ""
                          }`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employeecode ?? r.EmployeeCode ?? r.employeecode}
                          </td>

                          <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employee ?? r.Employee ?? r.employeename}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.Designation ?? r.fld_designationname ?? r.designationname}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.Branch ?? r.fld_branchname ?? r.branchname}
                          </td>

                          {vis.gradeVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {r.Grade ?? r.fld_gradename ?? r.gradename}
                            </td>
                          )}

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.__workingDays}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.DaysWorked ?? r.daysworked ?? "0"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center font-bold ${redRow ? "text-red-600" : ""} ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {lopDays}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.grosssalary ?? r.grossSalary ?? r.GrossSalary ?? "0"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.BasicPay ?? r.basicpay ?? "0"}
                          </td>

                          {vis.paidBasicVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {r.PaidBasic ?? r.paidbasic ?? "0"}
                            </td>
                          )}

                          {vis.conveyanceVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {r.Conveyance ?? r.conveyance ?? "0"}
                            </td>
                          )}

                          {vis.educationalVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {r.Educational ?? r.educational ?? "0"}
                            </td>
                          )}

                          {vis.medicalVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {r.Medical ?? r.medical ?? "0"}
                            </td>
                          )}

                          {vis.hraVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {r.HRA ?? r.hra ?? "0"}
                            </td>
                          )}

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.PF ?? r.pf ?? "0"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.ESI ?? r.esi ?? "0"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.advance ?? r.Advance ?? "0"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.LoanAmt ?? r.loan ?? r.Loan ?? "0"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.LoanDetection ?? r.loandetection ?? "0"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.BalanceLoanAmt ?? r.balloan ?? "0"}
                          </td>

                          {vis.lopAmtVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {r.LOPAmt ?? r.lopamt ?? "0"}
                            </td>
                          )}

                          {/* TDS editable */}
                          <td className="px-4 py-4 text-center">
                            <input
                              className={`w-24 px-3 py-2 rounded-xl border outline-none ${
                                isDark
                                  ? "bg-gray-800/60 border-white/10 text-white"
                                  : "bg-white border-gray-200 text-gray-900"
                              }`}
                              value={r.__tds}
                              onChange={(e) => updateRow(empId, { __tds: e.target.value })}
                              inputMode="decimal"
                            />
                          </td>

                          {/* Other Deductions editable */}
                          <td className="px-4 py-4 text-center">
                            <input
                              className={`w-24 px-3 py-2 rounded-xl border outline-none ${
                                isDark
                                  ? "bg-gray-800/60 border-white/10 text-white"
                                  : "bg-white border-gray-200 text-gray-900"
                              }`}
                              value={r.__othersDeduction}
                              onChange={(e) => updateRow(empId, { __othersDeduction: e.target.value })}
                              inputMode="decimal"
                            />
                          </td>

                          {/* Net Pay */}
                          <td className={`px-4 py-4 text-sm text-center font-extrabold ${isDark ? "text-emerald-200" : "text-emerald-700"}`}>
                            {num(r.__netpay, 0).toFixed(2)}
                          </td>

                          {/* Select */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(empId)}
                              onChange={() => toggleSelect(empId)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination + submit */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* pagination */}
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => {
                      const next = Math.max(1, page - 1);
                      setPage(next);
                      fetchGrid(next);
                    }}
                    disabled={page === 1 || loading}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1 || loading
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                    type="button"
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        const next = i + 1;
                        setPage(next);
                        fetchGrid(next);
                      }}
                      disabled={loading}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1 ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                      type="button"
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => {
                      const next = Math.min(totalPages, page + 1);
                      setPage(next);
                      fetchGrid(next);
                    }}
                    disabled={page === totalPages || loading}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages || loading
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={handleSubmit} disabled={selectedIds.length === 0 || !canGenerate || loading} className={submitBtn} type="button">
                <Send className="w-4 h-4" />
                Submit Selected
              </button>
            </div>
          </div>

          {/* small selected badge */}
          <div className="mt-4 flex items-center gap-3">
            <div
              className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <CheckSquare className="w-4 h-4" />
                Selected: {selectedIds.length}
              </span>
            </div>

            {month !== "Select" && year !== "Select" && (
              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-indigo-900/40 text-indigo-100" : "bg-indigo-100 text-indigo-800"
                }`}
              >
                Period: {monthName(month)} {year}
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default PayrollEntry;
